/**
 * Shared dashboard / work-order visibility gating for web and mobile.
 *
 * Single source of truth so the two platforms can't drift. Mirrors the backend
 * enforcement exactly:
 *  - Location & executive dashboards are gated by RequirePermission (strict):
 *    only the explicit permission (or system:admin) grants access.
 *  - Work orders are gated by CheckPermission, which ALSO allows users with NO
 *    permissions at all (nil-perms = no-role / department-only users, the
 *    department-access path). The UI must mirror that, otherwise it would hide
 *    a tab the backend allows (or show one it 403s).
 *
 * Permissions are resolved per selected location (the JWT is re-issued with
 * location-scoped permissions on location switch), so passing the current
 * `permissions` array yields the gates for the selected location.
 */
import { hasExactPermission, SYSTEM_ADMIN_PERMISSION, ACCOUNT_ADMIN_PERMISSION } from './permissionChecks';

export interface DashboardGateUser {
  role?: string | null;
}

export interface DashboardGates {
  isSystemAdmin: boolean;
  isAccountAdmin: boolean;
  /** Executive (account) dashboard — strict: needs the permission. Caller also checks account context. */
  canViewExecutiveDashboard: boolean;
  /** Location ("Departments"/"Locations") dashboard — strict: needs the permission. */
  canViewLocationDashboard: boolean;
  /** Work orders — mirrors backend CheckPermission: admin, OR workorders:read, OR nil-perms (department-only). */
  canViewWorkOrders: boolean;
}

export const DASHBOARD_PERMISSIONS = {
  executive: 'dashboards:executive:view',
  location: 'dashboards:location:view',
  workOrdersRead: 'workorders:read',
} as const;

export function getDashboardGates(user: DashboardGateUser | null | undefined, permissions: string[] | null | undefined): DashboardGates {
  const perms = permissions || [];
  const isSystemAdmin = hasExactPermission(perms, SYSTEM_ADMIN_PERMISSION) || user?.role === 'super_admin';
  const isAccountAdmin = hasExactPermission(perms, ACCOUNT_ADMIN_PERMISSION) || user?.role === 'account_admin';

  return {
    isSystemAdmin,
    isAccountAdmin,
    // Strict (RequirePermission): only system admins bypass server-side.
    canViewExecutiveDashboard: isSystemAdmin || perms.includes(DASHBOARD_PERMISSIONS.executive),
    canViewLocationDashboard: isSystemAdmin || perms.includes(DASHBOARD_PERMISSIONS.location),
    // CheckPermission parity: admins, holders of workorders:read, OR users with
    // no permissions at all (department-only users rely on the nil-perms bypass).
    canViewWorkOrders: isSystemAdmin || isAccountAdmin || perms.length === 0 || perms.includes(DASHBOARD_PERMISSIONS.workOrdersRead),
  };
}

/**
 * Whether the executive (account) dashboard should be VISIBLE for this user.
 *
 * Mirrors the backend exactly: GET /accounts/dashboard is gated by
 * RequirePermission("dashboards:executive:view") alone — the account is
 * resolved from the JWT, so holding the permission is sufficient. In
 * particular, the permission may arrive via a per-user permission OVERRIDE,
 * which creates NO account-scope role assignment; requiring an account role
 * here (as the UI historically did) hides a dashboard the backend serves.
 */
export function canShowExecutiveDashboard(user: DashboardGateUser | null | undefined, permissions: string[] | null | undefined): boolean {
  return getDashboardGates(user, permissions).canViewExecutiveDashboard;
}
